import { useEffect } from "react";
import { FormLabel, RadioGroup, Radio as MuiRadio, FormHelperText } from "@mui/material";
import type { RadioGroupProps } from "@mui/material/RadioGroup";
import { get } from "lodash";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import type { FormControlLabelProps } from "@mui/material/FormControlLabel";
import { useFormikContext } from "formik";
import type { CSSProperties, ReactNode } from "react";

import { useFormContext } from "@/hooks/useFormContext";
import { FormSkeleton } from "../../skeleton";

type FormValues = Record<string, unknown>;

export interface RadioProps extends RadioGroupProps {
  label?: string;
  alignLabel?: CSSProperties["textAlign"];
  loading?: boolean;
  error?: string;
}

export interface RadioItemProps {
  value: FormControlLabelProps["value"];
  label?: ReactNode;
}

function RadioItem({ value, label }: RadioItemProps) {
  return <FormControlLabel value={value} control={<MuiRadio />} label={label} />;
}

function RadioBase({
  name,
  label,
  onChange,
  value,
  defaultValue,
  alignLabel = "inherit",
  loading,
  sx,
  children,
  error,
}: RadioProps) {
  const formik = useFormikContext<FormValues>();
  const formContext = useFormContext();
  const fieldName = name as string;

  useEffect(() => {
    formik.setFieldError(fieldName, error);
  }, [error]);

  return (
    <FormSkeleton loading={loading ?? formContext?.loading}>
      <FormControl sx={{ width: "100%" }} error={Boolean(get(formik.errors, fieldName))}>
        {label && <FormLabel sx={{ textAlign: alignLabel }}>{label}</FormLabel>}
        <RadioGroup
          defaultValue={defaultValue}
          name={name}
          value={value ?? get(formik.values, fieldName)}
          onChange={onChange ?? formik.handleChange}
          sx={sx}
        >
          {children}
        </RadioGroup>
        {Boolean(get(formik.errors, fieldName)) && (
          <FormHelperText>{get(formik.errors, fieldName) as ReactNode}</FormHelperText>
        )}
      </FormControl>
    </FormSkeleton>
  );
}

type RadioComponent = typeof RadioBase & {
  Item: typeof RadioItem;
};

export const Radio = RadioBase as RadioComponent;
Radio.Item = RadioItem;
